import React from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography'
import { ticketColors } from '../../shared/theme/ticketColors';
import { buildCustomerOverdueWhatsApp, formatOverdueDaysLabel, getDunningLevel, getOverdueDays } from '../application/customerDunning';
import type { CustomerAccountRow } from '../domain/customerTypes';

type Props = {
  customer: CustomerAccountRow;
  hasPhone: boolean;
  formatMoney: (n: number) => string;
  onSendReminder?: () => void;
};

export function CustomerDetailDunningPanel({ customer, hasPhone, formatMoney, onSendReminder }: Props) {
  const dunning = getDunningLevel(customer);
  if (dunning === 'none') return null;

  const overdueDays = getOverdueDays(customer) ?? 0;
  const isUrgent = dunning === 'urgent';

  const messages: Record<string, { title: string; hint: string; waMsg: string }> = {
    r1: {
      title: 'Deuda vencida (1–15 días)',
      hint: `Venció hace ${formatOverdueDaysLabel(overdueDays)}. Mandale un recordatorio.`,
      waMsg: buildCustomerOverdueWhatsApp({
        name: customer.name,
        balanceLabel: formatMoney(customer.balance),
        overdueDays,
        level: 'r1',
      }),
    },
    r2: {
      title: 'Deuda vencida (16–30 días)',
      hint: `Venció hace ${formatOverdueDaysLabel(overdueDays)}. Ya pasó más de dos semanas.`,
      waMsg: buildCustomerOverdueWhatsApp({
        name: customer.name,
        balanceLabel: formatMoney(customer.balance),
        overdueDays,
        level: 'r2',
      }),
    },
    urgent: {
      title: 'Deuda crítica (+30 días)',
      hint: `Venció hace ${formatOverdueDaysLabel(overdueDays)}. Más de un mes. Priorizalo.`,
      waMsg: buildCustomerOverdueWhatsApp({
        name: customer.name,
        balanceLabel: formatMoney(customer.balance),
        overdueDays,
        level: 'urgent',
      }),
    },
  };
  const info = messages[dunning];
  if (!info) return null;

  return (
    <View style={[styles.dunningPanel, isUrgent && styles.dunningPanelUrgent]}>
      <View style={styles.dunningHeader}>
        <Ionicons
          name={isUrgent ? 'alert-circle-outline' : 'warning-outline'}
          size={16}
          color={isUrgent ? ticketColors.bad : ticketColors.gold}
        />
        <Text style={[styles.dunningTitle, isUrgent && styles.dunningTitleUrgent]}>
          {info.title}
        </Text>
      </View>
      <Text style={styles.dunningHint}>{info.hint}</Text>
      {hasPhone && onSendReminder ? (
        <TouchableOpacity
          style={[styles.dunningWaBtn, isUrgent && styles.dunningWaBtnUrgent]}
          onPress={onSendReminder}
          accessibilityRole="button"
          accessibilityLabel="Enviar recordatorio de mora por WhatsApp"
        >
          <Ionicons name="logo-whatsapp" size={14} color="#fff" />
          <Text style={styles.dunningWaBtnText}>Enviar recordatorio</Text>
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  dunningPanel: {
    gap: spacing.xs,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.warnBorder,
    backgroundColor: ticketColors.warnBg,
    padding: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  dunningPanelUrgent: {
    borderColor: ticketColors.bad,
  },
  dunningHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  dunningTitle: {
    ...typography.caption,
    fontWeight: '800',
    color: ticketColors.gold,
    flex: 1,
  },
  dunningTitleUrgent: {
    color: ticketColors.bad,
  },
  dunningHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  dunningWaBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    alignSelf: 'flex-start',
    marginTop: 2,
    backgroundColor: '#25D366',
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
  },
  dunningWaBtnUrgent: {
    backgroundColor: ticketColors.bad,
  },
  dunningWaBtnText: {
    ...typography.caption,
    fontWeight: '700',
    color: '#fff',
  },
});
